import { createSelector } from 'reselect';
import {
  compose,
  map,
  pluck,
  mean,
  sum,
  head,
  last,
  reduce,
  values,
  find,
  propEq,
  isNil,
  tail,
} from 'ramda';
import { Compare, Metric, MetricProp } from '../constants';
import { totalInterval } from '../utils/time';
import { getCompare, getSafeMetric, getSafeCrimeType } from './options';
import { getCrimeTypes } from '../reducers/crimeTypes';
import { getCityCrimes, getCensusCrimes } from '../reducers/crimes';
import { isCensusView } from './router';
import { getTotalTimespanInterval } from './intervals';

export const getCrimes = createSelector(
  isCensusView,
  getCityCrimes,
  getCensusCrimes,
  (censusView, cities, censusTracts) => (censusView ? censusTracts : cities),
);

export const getSingleValueTotal = createSelector(
  getCompare,
  getCrimes,
  (compare, crimes) =>
    map(d => {
      const allValues = pluck(MetricProp[Metric.TOTAL], d);
      return compare === Compare.NONE
        ? sum(allValues)
        : compare === Compare.RECURRING
          ? head(allValues) - mean(tail(allValues))
          : head(allValues) - last(allValues);
    }, crimes),
);

export const getTimeIntervalOnePopulation = createSelector(getCrimes, crimes =>
  map(d => last(d).population, crimes),
);

export const getTimeIntervalTwoPopulation = createSelector(getCrimes, crimes =>
  map(d => head(d).population, crimes),
);

export const getTimeIntervalOneRate = createSelector(getCrimes, crimes =>
  map(d => last(d).rate, crimes),
);

export const getTimeIntervalTwoRate = createSelector(getCrimes, crimes =>
  map(d => head(d).rate, crimes),
);

export const getTimeIntervalOneCrimes = createSelector(getCrimes, crimes =>
  map(d => last(d).crimes, crimes),
);

export const getTimeIntervalTwoCrimes = createSelector(getCrimes, crimes =>
  map(d => head(d).crimes, crimes),
);

export const getSingleValueRate = createSelector(
  isCensusView,
  getCompare,
  getCrimes,
  (censusView, compare, crimes) =>
    map(d => {
      const allValues = pluck(MetricProp[Metric.RATE], d);
      return [Compare.NONE, Compare.RECURRING].indexOf(compare) >= 0
        ? censusView ? mean(allValues) * 100 : mean(allValues)
        : censusView
          ? head(allValues) - last(allValues) * 100
          : head(allValues) - last(allValues);
    }, crimes),
);

/*
 * Get one value summary of data for provided compare mode and metric
 */
export const getSingleValueData = createSelector(
  getSafeMetric,
  getSingleValueTotal,
  getSingleValueRate,
  (metric, total, rate) => (metric === Metric.TOTAL ? total : rate),
);

export const getTimeIntervals = createSelector(getCrimes, crimes =>
  map(d => totalInterval(pluck('timespan', d)))(crimes),
);

export const getEarliestDate = createSelector(getTimeIntervals, timeIntervals =>
  reduce(
    (earliest, date) =>
      earliest < date.split('/')[0] ? earliest : date.split('/')[0],
    null,
  )(values(timeIntervals)),
);

export const getLatestDate = createSelector(getTimeIntervals, timeIntervals =>
  reduce(
    (latest, date) =>
      latest > date.split('/')[1] ? latest : date.split('/')[1],
    null,
  )(values(timeIntervals)),
);

export const getTimespanIntervalWithData = createSelector(
  getTimeIntervals,
  getEarliestDate,
  getLatestDate,
  getTotalTimespanInterval,
  (timeIntervals, earliest, latest, timespan) =>
    Object.keys(timeIntervals).length === 0
      ? timespan
      : `${earliest}/${latest}`,
);

const getMeanPopulation = compose(Math.round, mean, pluck('population'));

export const getPopulationData = createSelector(
  getCrimes,
  map(getMeanPopulation),
);

export const getHighlightTimestamps = createSelector(
  getCompare,
  getSafeMetric,
  getCrimes,
  (compare, metric, crimes) =>
    map(
      d =>
        compare === Compare.NONE || compare === Compare.RECURRING
          ? null
          : [head(d), last(d)].map(record => ({
              timespan: record.timespan,
              value: record[MetricProp[metric]],
            })),
    )(crimes),
);

export const getCrimeTypeText = createSelector(
  getCrimeTypes,
  getSafeCrimeType,
  getSafeMetric,
  (crimeTypes, crimeType, metric) => {
    let pair = find(propEq('id', parseInt(crimeType, 10)))(crimeTypes);
    return isNil(pair) ? 'murders' : pair.name;
  },
);



// WEBPACK FOOTER //
// ./src/selectors/crimes.js